import React from "react";
import { Button, SelectOption } from "../../../ui";
import "./multiSelectFilter.scss";

interface MultiSelectFilterProps {
  /** Id from the surrounding Field, so its label names the trigger. */
  id?: string;
  "aria-describedby"?: string;
  options: SelectOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  /** Trigger text when nothing is selected. */
  placeholder: string;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
}

/**
 * Checkbox menu for filters that take several values. src/ui has no multi-select yet;
 * this stays local to Transactions until one is added there.
 */
const MultiSelectFilter: React.FC<MultiSelectFilterProps> = ({
  id,
  "aria-describedby": describedBy,
  options,
  selected,
  onChange,
  placeholder,
  open,
  onToggle,
  onClose,
}) => {
  const menuId = `${id ?? "multi-select"}-menu`;
  const summary =
    selected.length === 0
      ? placeholder
      : options
          .filter((o) => selected.includes(o.value))
          .map((o) => o.label)
          .join(", ");

  return (
    // Mouse-downs inside stay inside, so the page's outside-click handler doesn't close the menu.
    <div
      className="txn-multi"
      onMouseDown={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        if (e.key === "Escape" && open) onClose();
      }}
    >
      <button
        type="button"
        id={id}
        className="ds-select txn-multi__trigger"
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-describedby={describedBy}
        onClick={onToggle}
      >
        <span className={selected.length === 0 ? "txn-multi__value ds-text-subtle" : "txn-multi__value"}>
          {summary}
        </span>
      </button>
      {open && (
        <div id={menuId} className="txn-multi__menu">
          {options.map((o) => (
            <label key={o.value} className="txn-multi__option">
              <input
                type="checkbox"
                checked={selected.includes(o.value)}
                onChange={(e) =>
                  onChange(e.target.checked ? [...selected, o.value] : selected.filter((v) => v !== o.value))
                }
              />
              {o.label}
            </label>
          ))}
          {selected.length > 0 && (
            <div className="txn-multi__footer">
              <Button size="sm" variant="ghost" onClick={() => onChange([])}>
                Clear
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiSelectFilter;
